import { Category, CategoryType } from "@switcherstudio/switcher-api-client";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { RootState } from "store/reducers";
import { AppDispatch } from "store/store";
import rollbar from "helpers/rollbar";
import { slugify } from "helpers/slugify";
import { useClaimCheck } from "hooks/useClaimCheck";
import { BaseAssignment } from "../base-assignment";

interface CategoriesAssignmentProps {
    categoryType: CategoryType;
    selectedCategories: Category[];
    setSelectedCategories: (categories: Category[]) => void;
}

export const CategoriesAssignment = ({
    categoryType,
    selectedCategories,
    setSelectedCategories
}: CategoriesAssignmentProps) => {
    const { t } = useTranslation();
    const hasCatalogClaim = useClaimCheck("catalog");
    const userInfo = useSelector((s: RootState) => s.user?.userInfo);
    const dispatch = useDispatch<AppDispatch>();

    const { data: userCategories, dispatchApiRequest: refetchCategories } =
        useSwitcherClient((client) => client.categories_GetCategories, {
            requestImmediately: true,
            args: [userInfo?.MetaData?.Details?.ProjectId, categoryType],
            onError: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: `${
                            hasCatalogClaim
                                ? t("video-upload:errors:tag-retrieval-error")
                                : t(
                                      "video-upload:errors:category-retrieval-error"
                                  )
                        }`
                    })
                );
            }
        });

    const { dispatchApiRequest: addUserCategory } = useSwitcherClient(
        (client) => client.categories_CreateCategories,
        {
            requestImmediately: false,
            onSuccess: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: `${
                            hasCatalogClaim
                                ? t("video-upload:errors:tag-add-success")
                                : t("video-upload:errors:category-add-success")
                        }`
                    })
                );
            },
            onError: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: `${
                            hasCatalogClaim
                                ? t("video-upload:errors:tag-add-error")
                                : t("video-upload:errors:category-add-error")
                        }`
                    })
                );
            }
        }
    );

    const handleRemoveCategory = (categoryId: string) => {
        const uc = selectedCategories.filter((c) => c.Id !== categoryId);
        setSelectedCategories(uc);
    };

    const setSelectedCategoriesFromDropdown = useCallback(
        (category: Category) => {
            !selectedCategories.find((c) => c.Id === category.Id) &&
                setSelectedCategories([
                    ...selectedCategories,
                    userCategories.Categories.find(
                        (uc) => uc?.Id === category.Id
                    )
                ]);
        },
        [userCategories?.Categories, selectedCategories, setSelectedCategories]
    );

    const setSelectedCategoriesFromUserInput = useCallback(
        (category: Category) => {
            setSelectedCategories([...selectedCategories, category]);
        },
        [selectedCategories, setSelectedCategories]
    );

    const handleAddCategory = useCallback(
        async (newCategory: string) => {
            const existingCat = userCategories?.Categories?.find(
                (c) => slugify(c.Name) === slugify(newCategory)
            );
            if (newCategory !== "" && existingCat) {
                setSelectedCategoriesFromDropdown(existingCat);
            } else if (newCategory !== "") {
                try {
                    const categoriesResponse = await addUserCategory([
                        {
                            Categories: [
                                {
                                    Name: newCategory,
                                    ProjectId:
                                        userInfo?.MetaData?.Details?.ProjectId,
                                    CategoryType: categoryType as any
                                }
                            ]
                        }
                    ]);
                    refetchCategories();
                    const addedCat = categoriesResponse?.Categories?.find(
                        (c) => c.Name === newCategory
                    );
                    if (addedCat) {
                        setSelectedCategoriesFromUserInput(addedCat);
                    }
                } catch (e) {
                    rollbar.error("Error adding user category", e);
                }
            }
        },
        [
            addUserCategory,
            userInfo?.MetaData?.Details?.ProjectId,
            userCategories?.Categories,
            categoryType,
            refetchCategories,
            setSelectedCategoriesFromDropdown,
            setSelectedCategoriesFromUserInput
        ]
    );

    return (
        <BaseAssignment<Category>
            items={userCategories?.Categories || []}
            itemIdKey={"Id"}
            itemLabelKey={"Name"}
            assignedItems={selectedCategories}
            handleAddItem={handleAddCategory}
            handleAssignItem={setSelectedCategoriesFromDropdown}
            handleRemoveItem={handleRemoveCategory}
            labels={{
                appliedItemsLabel: hasCatalogClaim
                    ? t("video-upload:tags")
                    : t("video-upload:categories"),
                noItemsAppliedLabel: hasCatalogClaim
                    ? t("video-upload:no-tags-applied")
                    : t("video-upload:no-categories-applied"),
                selectLabel: hasCatalogClaim
                    ? t("video-upload:select-tag")
                    : t("video-upload:select-category"),
                selectPlaceholder: t("broadcasts-multi-select:select"),
                inputLabel: hasCatalogClaim
                    ? t("video-upload:new-tag")
                    : t("video-upload:new-category"),
                inputPlaceholder: null,
                addLabel: t("video-upload:add")
            }}
            variant={"add"}
        />
    );
};
